import { Control, Pointer } from '@yleisradio/areena-types';
import { useState } from 'react';
import { usePlayerState } from 'contexts/PlayerStateContext';
import { getAvailablePlayerByPointer } from 'services/areena-api/fetchers';
import logger from 'services/logger';
import { Action } from './type';
import { toastPlaybackError } from 'components/Notifications';
import { useUILanguage } from 'hooks/useUILanguage';
import { useLocationParameters } from 'hooks/useLocationParameters';
import { useRouter } from 'next/router';
import { pointerToClientURL } from 'utils/pointer';
import { useAreenaService } from 'contexts/AreenaServiceContext';

type PlayerActionForControlProps = {
  control: Control | null;
  cardPointer?: Pointer | undefined;
  seek?: number | undefined;
};

export function usePlayerActionForControl({
  control,
  cardPointer,
  seek,
}: PlayerActionForControlProps): Action {
  const pointer = control?.destination;

  return usePlayerActionForPointer({ pointer, cardPointer, seek });
}

type PlayerActionForPointerProps = {
  pointer: Pointer | undefined;
  cardPointer: Pointer | undefined;
  seek?: number | undefined;
};

export function usePlayerActionForPointer({
  pointer,
  cardPointer,
  seek,
}: PlayerActionForPointerProps): Action {
  const { areenaService: service = 'tv' } = useAreenaService();
  const {
    closePlayer,
    setIsPlaying,
    startNewSession,
    isInPipMode,
    setIsInPipMode,
  } = usePlayerState();
  const [isExecuting, setIsExecuting] = useState(false);
  const language = useUILanguage();
  const locationParameters = useLocationParameters();
  const { push: goTo } = useRouter();

  async function playerAction() {
    if (isInPipMode) {
      setIsInPipMode(false);
      closePlayer();
    }
    if (isExecuting) {
      return;
    }

    if (!pointer) {
      logger.error(new Error('Pointer needs to be defined'));
      toastPlaybackError(language);
      return;
    }

    if (!locationParameters) {
      logger.error(new Error('Location parameters need to be defined'));
      toastPlaybackError(language);
      return;
    }

    setIsExecuting(true);

    try {
      const player = await getAvailablePlayerByPointer(
        pointer,
        locationParameters
      );

      if (player) {
        startNewSession(player, seek);
        setIsPlaying(true);
      } else {
        closePlayer();

        const fallbackUrl =
          cardPointer && pointerToClientURL(cardPointer, language, service);

        if (fallbackUrl) {
          goTo(fallbackUrl);
        } else {
          toastPlaybackError(language);
        }
      }
    } catch (error) {
      logger.error(error, 'Failed to get player');
      closePlayer();
      toastPlaybackError(language);
    }

    setIsExecuting(false);
  }
  return {
    runAction: playerAction,
    executing: false,
  };
}
